/**
 * Roue de loterie — logique pure (Boîte à outils, 1re → Tle).
 *
 * Une roue partagée en secteurs (en douzièmes de tour) ; chaque secteur fait
 * gagner une somme, la partie coûte une mise. Le gain net X = gain − mise
 * est une variable aléatoire : sa loi (valeurs regroupées, probabilités en
 * fractions), son espérance E(X) = Σ xᵢ P(X = xᵢ), sa variance et son écart
 * type. Sur beaucoup de parties, le gain moyen se rapproche de E(X) ; le jeu
 * est équitable si E(X) = 0.
 */

import { add, frac, mul, sub, value, type Frac } from '../../ui/exact';
import { formatNumber } from '../../ui/format';
import { pickWeighted } from '../../ui/random';

export const PARTS = 12;

export interface Sector {
  /** nombre de douzièmes de la roue */
  k: number;
  /** somme gagnée (€), mise non déduite */
  gain: number;
}

export interface Game {
  stake: number;
  sectors: Sector[];
}

export interface Preset {
  id: string;
  label: string;
  game: Game;
}

export const PRESETS: readonly Preset[] = [
  {
    id: 'kermesse',
    label: 'Kermesse',
    game: {
      stake: 2,
      sectors: [
        { k: 1, gain: 10 },
        { k: 3, gain: 3 },
        { k: 8, gain: 0 },
      ],
    },
  },
  {
    id: 'half',
    label: 'Pile ou rien',
    game: {
      stake: 1,
      sectors: [
        { k: 6, gain: 2 },
        { k: 6, gain: 0 },
      ],
    },
  },
  {
    id: 'jackpot',
    label: 'Gros lot',
    game: {
      stake: 3,
      sectors: [
        { k: 1, gain: 20 },
        { k: 2, gain: 5 },
        { k: 4, gain: 2 },
        { k: 5, gain: 0 },
      ],
    },
  },
];

/**
 * Défis « Rends le jeu équitable » : la mise équitable tombe sur un nombre
 * entier d'euros (Σ gain × parts multiple de 12), et la mise de départ est
 * différente.
 */
export const FAIR_CHALLENGES: readonly Preset[] = [
  {
    id: 'fair-1',
    label: 'Défi 1',
    game: {
      stake: 3,
      sectors: [
        { k: 1, gain: 12 },
        { k: 3, gain: 4 },
        { k: 8, gain: 0 },
      ],
    },
  },
  {
    id: 'fair-2',
    label: 'Défi 2',
    game: {
      stake: 4,
      sectors: [
        { k: 1, gain: 20 },
        { k: 2, gain: 5 },
        { k: 3, gain: 2 },
        { k: 6, gain: 0 },
      ],
    },
  },
  {
    id: 'fair-3',
    label: 'Défi 3',
    game: {
      stake: 1,
      sectors: [
        { k: 2, gain: 6 },
        { k: 4, gain: 3 },
        { k: 6, gain: 0 },
      ],
    },
  },
];

export const totalParts = (g: Game) => g.sectors.reduce((s, x) => s + x.k, 0);

/** la roue est complète : les secteurs font un tour (12 douzièmes) */
export const complete = (g: Game) => totalParts(g) === PARTS && g.sectors.every((s) => s.k > 0);

export interface LawRow {
  /** gain net */
  x: number;
  p: Frac;
}

/** loi de X = gain − mise, valeurs regroupées et triées */
export function law(g: Game): LawRow[] {
  const map = new Map<number, number>();
  for (const s of g.sectors) map.set(s.gain - g.stake, (map.get(s.gain - g.stake) ?? 0) + s.k);
  return [...map.entries()].sort((a, b) => a[0] - b[0]).map(([x, k]) => ({ x, p: frac(k, PARTS) }));
}

export function expectation(g: Game): Frac {
  return law(g).reduce((s, r) => add(s, mul(frac(r.x), r.p)), frac(0));
}

export function variance(g: Game): Frac {
  const e = expectation(g);
  return law(g).reduce((s, r) => {
    const d = sub(frac(r.x), e);
    return add(s, mul(r.p, mul(d, d)));
  }, frac(0));
}

export const sd = (g: Game) => Math.sqrt(value(variance(g)));

/** σ exact si V est le carré d'un rationnel (V = 1 → σ = 1), sinon null */
export function sdExact(g: Game): string | null {
  const v = variance(g);
  const p = Math.round(Math.sqrt(v.p));
  const q = Math.round(Math.sqrt(v.q));
  return p * p === v.p && q * q === v.q ? fracText(frac(p, q)) : null;
}

/** « V(X) = E(X²) − E(X)² = 8,25 − (−5/12)² = 1163/144 ≈ 8,08 » */
export function varianceText(g: Game): string {
  const e = expectation(g);
  const e2 = law(g).reduce((s, r) => add(s, mul(frac(r.x * r.x), r.p)), frac(0));
  const v = variance(g);
  const vt = fracText(v);
  const et = fracText(e);
  const approx = vt.includes('/') ? ` ≈ ${formatNumber(value(v), 2).replace('-', '−')}` : '';
  const sq = e.p < 0 || et.includes('/') ? `(${et})` : et;
  return `V(X) = E(X²) − E(X)² = ${fracText(e2)} − ${sq}² = ${vt}${approx}`;
}

/**
 * Angle de la roue (en degrés, cumulé) pour amener le milieu du secteur i
 * sous l'aiguille, en haut, après au moins trois tours depuis `prev`.
 */
export function targetAngle(g: Game, i: number, prev: number): number {
  const before = g.sectors.slice(0, i).reduce((s, x) => s + x.k, 0);
  const mid = ((before + g.sectors[i]!.k / 2) / PARTS) * 360;
  return prev - (((prev % 360) + 360) % 360) + 360 * 3 + ((360 - mid) % 360);
}

/** un tour de roue : l'indice du secteur */
export function spin(g: Game, rng: () => number): number {
  return pickWeighted(
    g.sectors.map((s) => s.k),
    rng,
  );
}

/** gains nets de n parties */
export function play(g: Game, n: number, rng: () => number): number[] {
  return Array.from({ length: n }, () => g.sectors[spin(g, rng)]!.gain - g.stake);
}

/* ---------- écritures ---------- */

/** « 5/12 », « −0,25 », « 1 » : décimal si le dénominateur s'y prête */
export function fracText(f: Frac): string {
  let q = f.q;
  while (q % 2 === 0) q /= 2;
  while (q % 5 === 0) q /= 5;
  if (q === 1) return formatNumber(value(f), 6).replace('-', '−');
  return `${f.p < 0 ? '−' : ''}${Math.abs(f.p)}/${f.q}`;
}

/** « −1 € », « 8 € » */
export const euros = (v: number) => `${formatNumber(v, 2).replace('-', '−')}\u00a0€`;

/** une probabilité : toujours en fraction (« 1/4 », « 2/3 ») */
export const probText = (f: Frac) => (f.q === 1 ? String(f.p) : `${f.p}/${f.q}`);

/** « E(X) = (−2) × 2/3 + 1 × 1/4 + 8 × 1/12 = −5/12 ≈ −0,42 » */
export function expectationText(g: Game): string {
  const rows = law(g);
  const terms = rows.map(
    (r) => `${r.x < 0 ? `(${fracText(frac(r.x))})` : fracText(frac(r.x))} × ${probText(r.p)}`,
  );
  const e = expectation(g);
  const exact = fracText(e);
  const approx = exact.includes('/') ? ` ≈ ${formatNumber(value(e), 2).replace('-', '−')}` : '';
  return `E(X) = ${terms.join(' + ')} = ${exact}${approx}`;
}

export type Verdict = 'fair' | 'player' | 'organizer';

/** E(X) > 0 : favorable au joueur ; < 0 : à l'organisateur ; = 0 : équitable */
export function verdict(g: Game): Verdict {
  const e = expectation(g);
  return e.p === 0 ? 'fair' : e.p > 0 ? 'player' : 'organizer';
}

/** mise qui rend le jeu équitable : l'espérance du gain brut (peut ne pas tomber juste) */
export function fairStake(g: Game): Frac {
  return g.sectors.reduce((s, x) => add(s, mul(frac(x.gain), frac(x.k, PARTS))), frac(0));
}
